import { Actions, createEffect, ofType } from '@ngrx/effects'
import { SNACKBAR_LENGTH_SHORT, SnackbarService } from '@shared/utils/snackbar.service'
import { catchError, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { Store } from '@ngrx/store'
import { plainToInstance } from 'class-transformer'
import { ChangeRequestService } from '../../service/change-request.service'
import { ChangeRequestActions, ChangeRequestActionTypes } from '../actions/change-request.actions'
import { AuthSelectors } from '@app/core/store/selectors/auth.selector'
import { ChangeRequestSelectors } from '../selectors/change-request.selectors'
import { ChangeItemType, ChangeType } from '../../model'
import { isChangeRequestEnabled } from '@app/features/scope-overview/utils/is-change-request-enabled.utils'
import { selectQueryParams } from '@app/core/store/selectors/router.selector'

@Injectable()
export class ChangeRequestEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private router: Router,
    private changeRequestService: ChangeRequestService,
    private approvalFlowService: ApprovalFlowService,
    private snackbarService: SnackbarService
  ) {}

  createChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.createChangeRequest),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ payload, callback }, currentScope]) =>
        this.changeRequestService.createChangeRequest(currentScope.identity.id, payload).pipe(
          map((changeRequest: ChangeRequest) => {
            callback(changeRequest)
            return ChangeRequestActions.createChangeRequestSuccess({ changeRequest })
          }),
          catchError((error: unknown) => {
            callback()
            this.snackbarService.showDefaultErrorSnackbar()
            console.error(JSON.stringify(error))
            return of(ChangeRequestActions.createChangeRequestFail({ error: error as Error }))
          })
        )
      )
    )
  )

  createChangeRequestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChangeRequestActions.createChangeRequestSuccess),
        withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
        tap(([{ changeRequest }, currentScope]) => {
          this.approvalFlowService.updateApprovalStateChecks(
            plainToInstance(ScopeVersion, { ...currentScope, ...changeRequest })
          )
          this.router.navigate(this.getChangeRequestUrl(currentScope.identity.id, changeRequest.id))
          this.snackbarService.showInfoSnackbar(
            `Change Request ${changeRequest?.name} created successfully`,
            SNACKBAR_LENGTH_SHORT
          )
        })
      ),
    { dispatch: false }
  )

  getChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.getChangeRequest),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ scopeId, changeRequestId }, currentScope]) =>
        this.changeRequestService.getChangeRequest(scopeId || currentScope.identity.id, changeRequestId).pipe(
          map((changeRequest: ChangeRequest) => ChangeRequestActions.getChangeRequestSuccess({ changeRequest })),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred during loading change request')
            return of(ChangeRequestActions.getChangeRequestFail({ error: error as Error }))
          })
        )
      )
    )
  )

  getChangeRequestSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ChangeRequestActions.getChangeRequestSuccess,
        ChangeRequestActions.applyChangesSuccess,
        ChangeRequestActions.removeChangesSuccess
      ),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ChangeRequestSelectors.selectCurrentChangeRequest)
      ),
      map(([action, currentScope, currentChangeRequest]) => {
        let changeRequestId

        switch (action.type) {
          case ChangeRequestActionTypes.GET_CHANGE_REQUEST_SUCCESS:
            changeRequestId = action.changeRequest.id
            break
          default:
            changeRequestId = currentChangeRequest?.id
            break
        }

        return ChangeRequestActions.getChangeRequestPreview({
          scopeId: currentScope.identity.id,
          changeRequestId,
        })
      })
    )
  )

  getChangeRequestPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.getChangeRequestPreview),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ scopeId, changeRequestId }, currentScope]) =>
        this.changeRequestService.getChangeRequestPreview(scopeId || currentScope.identity.id, changeRequestId).pipe(
          map((previewScope: ScopeVersion) => ChangeRequestActions.getChangeRequestPreviewSuccess({ previewScope })),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred during loading change request preview')
            return of(ChangeRequestActions.getChangeRequestPreviewFail({ error: error as Error }))
          })
        )
      )
    )
  )

  getChangeRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.getChangeRequests),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(AuthSelectors.selectUser)
      ),
      filter(([_, currentScope, user]) => isChangeRequestEnabled(currentScope, user)),
      switchMap(([_, currentScope]) =>
        this.changeRequestService.getChangeRequests(currentScope.identity.id).pipe(
          map((changeRequests: ChangeRequest[]) => ChangeRequestActions.getChangeRequestsSuccess({ changeRequests })),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred during loading change requests')
            return of(ChangeRequestActions.getChangeRequestsFail({ error: error as Error }))
          })
        )
      )
    )
  )

  setCurrentChangeRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChangeRequestActions.setCurrentChangeRequest),
        withLatestFrom(
          this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
          this.store$.select(selectQueryParams)
        ),
        tap(([{ changeRequest }, currentScope, queryParams]) => {
          const url = this.getChangeRequestUrl(currentScope.identity.id, changeRequest?.id)
          this.router.navigate(url, {
            replaceUrl: false,
            state: { skipWaitForLoad: true },
            queryParams,
          })

          this.snackbarService.showInfoSnackbar(
            changeRequest ? `Switched to ${changeRequest?.name}` : 'Returned to main scope view',
            SNACKBAR_LENGTH_SHORT
          )
        })
      ),
    { dispatch: false }
  )

  saveScenarioConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.saveScenarioConfig),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([payload, currentScope]) => {
        const { scopeId, changeRequestId, configuration, componentModifiers, changeItemId } = payload

        const changeItemRequest = {
          changeType: ChangeType.MODIFIED,
          type: ChangeItemType.SCENARIO_CONFIGURATION,
          data: {
            scenarioConfiguration: {
              old: currentScope.configuration,
              new: configuration,
            },
            componentModifiers: {
              old: currentScope.componentModifierValues,
              new: componentModifiers,
            },
          },
        }

        const serviceCall = changeItemId
          ? this.changeRequestService.modifyChange(scopeId, changeRequestId, changeItemId, changeItemRequest)
          : this.changeRequestService.applyChanges(scopeId, changeRequestId, changeItemRequest)

        return serviceCall.pipe(
          map((changeItem: any) => {
            this.snackbarService.showSuccessSnackbar('Scenario configuration saved successfully')
            return ChangeRequestActions.saveScenarioConfigSuccess({ changeItem })
          }),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred while saving scenario configuration')
            return of(ChangeRequestActions.saveScenarioConfigFail({ error: error as Error }))
          })
        )
      })
    )
  )

  generateChangesFromScenarioConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.generateChangesFromScenarioConfig),
      switchMap(({ scopeId, changeRequestId }) =>
        this.changeRequestService.generateChangesFromScenarioConfiguration(scopeId, changeRequestId).pipe(
          map((changeRequest: ChangeRequest) => {
            this.snackbarService.showSuccessSnackbar('Changes generated from scenario configuration')
            return ChangeRequestActions.generateChangesFromScenarioConfigSuccess({ changeRequest })
          }),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar(
              'An error occurred while generating changes from scenario configuration'
            )
            return of(ChangeRequestActions.generateChangesFromScenarioConfigFail({ error: error as Error }))
          })
        )
      )
    )
  )

  getNetChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.getNetChanges),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ scopeId, changeRequestId }, currentScope]) =>
        this.changeRequestService.getNetChanges(scopeId || currentScope.identity.id, changeRequestId).pipe(
          map((netChangesScope: ScopeVersion) => ChangeRequestActions.getNetChangesSuccess({ netChangesScope })),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred while loading net changes')
            return of(ChangeRequestActions.getNetChangesFail({ error: error as Error }))
          })
        )
      )
    )
  )

  private getChangeRequestUrl(scopeId: number, changeRequestId: number) {
    const basicUrl = ['scopes', 'scope-overview', scopeId]
    if (changeRequestId) return [...basicUrl, 'change-request', changeRequestId]
    return basicUrl
  }
}
